import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material"
import React, { useCallback, useContext, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { customPalette } from "../../../../../../../theme"
import CancelButton from "../../../../../../components/buttons/CancelButton/CancelButton"
import { SubmitButton } from "../../../../../../components/buttons/SubmitButton/SubmitButton"
import { Transition } from "../../../../../../components/dialog/BaseDialog"
import { FormNumberInput } from "../../../../../../components/inputs/number-input/FormNumberInput"
import { FormMultilineTextInput } from "../../../../../../components/inputs/text-input/FormMultilineTextInput"
import { FormTextInput } from "../../../../../../components/inputs/text-input/FormTextInput"
import { SuccessContext } from "../../../../../../components/layout/success-snackbar"
import { pagesUrl } from "../../../../../../core/appConstants"
import { BSVariantContext } from "../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { BSVariantListContext } from "../../../../../../core/context/beem-shot/BSVariant/BSVariantListContext"
import { ProjectContext } from "../../../../../../core/context/project/ProjectContext"
import { BSVariant } from "../../../../../../core/dto/beem-shot/BSVariant/BSVariant"
import { BSVariantCreationDto } from "../../../../../../core/dto/beem-shot/BSVariant/BSVariantCreateDto"
import { BSVariantPatchDto } from "../../../../../../core/dto/beem-shot/BSVariant/BSVariantNameAndDescriptionPatchDto"
import { useForm } from "../../../../../../core/hooks/form/use-form"
import { nonZero, required } from "../../../../../../core/hooks/form/validation"
import { eventFromValue } from "../../../../../../core/services/helper-service"
import { resolveUrl } from "../../../../../../core/services/http-service"
import { SeisimicStatusCard } from "../../../beem-shot/variant/components/BSVariant/SeisimicStatusCard"
import { SelectInputStringNoneOption } from "../../../../../../components/inputs/select-input/SelectInputStringNoneOption"
import { SelectStrOption } from "../../../../../../components/inputs/select-input/SelectStrOption"

interface IForm {
  name: string
  description: string
  surfaceParcelle: number
  baseVariantId: string | null
}

function formToDto(form: IForm, projectId: string): BSVariantCreationDto {
  return {
    name: form.name,
    description: form.description,
    projectId,
    surfaceParcelle: form.surfaceParcelle,
  }
}

function formToDtoUpdate(form: IForm, variantId: string): BSVariantPatchDto {
  return {
    id: variantId,
    name: form.name,
    description: form.description,
    surfaceParcelle: form.surfaceParcelle,
  }
}

function dtoToForm(dto: BSVariant | undefined): IForm {
  return dto
    ? {
        name: dto.name,
        description: dto.description,
        surfaceParcelle: dto.surfaceParcelle,
        baseVariantId: dto.id ?? null,
      }
    : {
        name: "",
        description: "",
        surfaceParcelle: 0,
        baseVariantId: null,
      }
}

interface IProps {
  open: boolean
  bsVariant: BSVariant | undefined
  projectId?: string
  isUpdateMode?: boolean

  handleClose(): void
}

export function BSVariantFormDialog({
  open,
  bsVariant,
  handleClose,
  projectId,
  isUpdateMode = false,
}: Readonly<IProps>): React.JSX.Element {
  const navigate = useNavigate()
  const openSuccessSnackbar = useContext(SuccessContext)
  const { createVariant, updateBSVariantNameAndDescription } = useContext(BSVariantContext)
  const { bsVariants, fetchAllBSVariants } = useContext(BSVariantListContext)
  const { project } = useContext(ProjectContext)

  // Only for updating an existing variant
  const baseVariant: BSVariant | undefined = useMemo(() => (isUpdateMode ? bsVariant : undefined), [isUpdateMode, bsVariant])

  const options: SelectStrOption[] = useMemo(
    () =>
      bsVariants.map((_bsVariant) => ({
        label: _bsVariant.name,
        value: _bsVariant.id,
      })),
    [bsVariants]
  )

  const submit: (form: IForm) => Promise<any> = useCallback(
    (form: IForm) => {
      if (isUpdateMode && project?.id) {
        return updateBSVariantNameAndDescription(formToDtoUpdate(form, project.id)).then((updatedVariant) => {
          handleClose()
          navigate(resolveUrl(pagesUrl.VARIANT_DETAILS, [projectId, updatedVariant?.id]))
          openSuccessSnackbar("Les informations de l'estimation ont été mis à jour.")
        })
      } else if (project?.id) {
        return createVariant(formToDto(form, project.id), form.baseVariantId).then((createdVariant) => {
          handleClose()
          if (project.id) {
            fetchAllBSVariants(project.id).then(() => {
              navigate(resolveUrl(pagesUrl.VARIANT_DETAILS, [project.id, createdVariant.id]))
            })
          }
          return createdVariant
        })
      }
      return Promise.resolve()
    },
    [
      isUpdateMode,
      project?.id,
      navigate,
      projectId,
      openSuccessSnackbar,
      createVariant,
      updateBSVariantNameAndDescription,
      handleClose,
      fetchAllBSVariants,
    ]
  )

  const { form, errors, handleChange, handleSubmit, resetForm, setForm, isSubmitting } = useForm(
    baseVariant,
    dtoToForm,
    [required("name"), required("surfaceParcelle"), nonZero("surfaceParcelle")],
    submit
  )

  useEffect(() => {
    resetForm()
    if (bsVariants?.length === 0 && open) {
      setForm((oldForm) => ({ ...oldForm, name: "Estimation de base" }))
    }
  }, [projectId, bsVariants, open, resetForm, setForm])

  const onClose: () => void = useCallback(() => {
    handleClose()
    resetForm()
  }, [handleClose, resetForm])

  // Fill surface parcelle from the selected source variant
  useEffect(() => {
    if (!isUpdateMode) {
      const variantFound = bsVariants.find((variant) => variant.id === form.baseVariantId)
      if (variantFound) {
        handleChange(eventFromValue("surfaceParcelle", variantFound.surfaceParcelle))
      } else {
        handleChange(eventFromValue("surfaceParcelle", 0))
      }
    }
  }, [bsVariants, form.baseVariantId, handleChange, isUpdateMode])

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: customPalette.white,
        },
      }}
      TransitionComponent={Transition}>
      <Box component="form" id={isUpdateMode ? "update-bs-variant" : "create-bs-variant"} onSubmit={handleSubmit}>
        <DialogTitle variant="subtitle2">
          <Typography fontSize={24} fontWeight={600}>
            {isUpdateMode ? "Modifier l'estimation" : "Nouvelle estimation Shot"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Informations</Typography>
            </Grid>

            <Grid item xs={12}>
              <FormTextInput
                fieldName="name"
                label="Nom de la nouvelle estimation"
                handleChange={handleChange}
                errors={errors}
                form={form}
              />
            </Grid>

            <Grid item xs={12}>
              <FormNumberInput
                fieldName="surfaceParcelle"
                label="Surface parcelle en m² *"
                errors={errors}
                form={form}
                handleEventChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormMultilineTextInput
                fieldName="description"
                label="Entrez la description de l'estimation"
                form={form}
                handleChange={handleChange}
                errors={errors}
                required={false}
              />
            </Grid>

            <Grid item xs={12}>
              {bsVariants.length !== 0 && !isUpdateMode && (
                <SelectInputStringNoneOption
                  fieldName="baseVariantId"
                  label="Généré à partir de"
                  form={form}
                  options={options}
                  handleChange={handleChange}
                  errors={errors}
                />
              )}
            </Grid>

         {false &&   <Grid item xs={12}>
              <SeisimicStatusCard />
            </Grid>}

            <Grid item xs={12} fontSize={12} fontWeight={400} sx={{ color: customPalette.subtitleGray }}>
              Les champs marqués d'un astérisque sont obligatoires.
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={onClose} label="Annuler" />
          {isUpdateMode ? (
            <SubmitButton label="Mettre à jour" formId="update-bs-variant" isSubmitting={isSubmitting} />
          ) : (
            <SubmitButton label="Créer" formId="create-bs-variant" isSubmitting={isSubmitting} />
          )}
        </DialogActions>
      </Box>
    </Dialog>
  )
}
